// Error handling utilities for PropWise
// Handles both mock and real AWS errors consistently

import { TimeoutError } from './fetchWithTimeout';
import { ApiError } from '../services/reportsApi';
import { HTTP_STATUS } from '../constants/api';
import { FILE_SIZE } from '../constants/upload';
import { PASSWORD_POLICY_MESSAGE } from './passwordPolicy';

export interface AppError {
  code: string;
  message: string;
  userMessage: string;
  retryable: boolean;
}

// General error codes
export const ERROR_CODES = {
  TIMEOUT: 'TIMEOUT',
  UPLOAD_CANCELLED: 'UPLOAD_CANCELLED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  UNAUTHORIZED: 'UNAUTHORIZED',
  // Renamed from ALPHA_ENTITLEMENT_DENIED: it now covers the grant model's
  // NO_RUNS as well as the alpha codes, and 'ALPHA' would be wrong the
  // moment that path retires.
  ENTITLEMENT_DENIED: 'ENTITLEMENT_DENIED',
  // The server's terms gate (BE-HND-03): POST /reports or begin-processing
  // refused a user who has not accepted the current terms. The caller
  // reopens the gate with recheckTerms.
  TERMS_REQUIRED: 'TERMS_REQUIRED',
  UPLOAD_ERROR: 'UPLOAD_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  // begin-processing's server-side count and size check (BE-SHR-05): the
  // listed PDFs break a limit in constants/upload.ts. Comes before the flip
  // and the charge, so the report stays PENDING and nothing is spent.
  UPLOAD_LIMIT_EXCEEDED: 'UPLOAD_LIMIT_EXCEEDED',
  REPORT_ID_COLLISION: 'REPORT_ID_COLLISION',
  NOT_FOUND: 'NOT_FOUND',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN',
} as const;

// Common auth error codes from Cognito
export const AUTH_ERROR_CODES = {
  USER_NOT_FOUND: 'UserNotFoundException',
  NOT_AUTHORIZED: 'NotAuthorizedException',
  USER_NOT_CONFIRMED: 'UserNotConfirmedException',
  PASSWORD_RESET_REQUIRED: 'PasswordResetRequiredException',
  NEW_PASSWORD_REQUIRED: 'NewPasswordRequiredException',
  INVALID_PASSWORD: 'InvalidPasswordException',
  TOO_MANY_REQUESTS: 'TooManyRequestsException',
  CODE_MISMATCH: 'CodeMismatchException',
  EXPIRED_CODE: 'ExpiredCodeException',
  LIMIT_EXCEEDED: 'LimitExceededException',
  // Self-serve signup (task 08)
  USERNAME_EXISTS: 'UsernameExistsException',
  CODE_DELIVERY_FAILURE: 'CodeDeliveryFailureException',
  INVALID_PARAMETER: 'InvalidParameterException',
  TOO_MANY_FAILED_ATTEMPTS: 'TooManyFailedAttemptsException',
  USER_LAMBDA_VALIDATION: 'UserLambdaValidationException',
  UNEXPECTED_LAMBDA: 'UnexpectedLambdaException',
  INVALID_LAMBDA_RESPONSE: 'InvalidLambdaResponseException'
} as const;

// Type guard for errors with code/name/message properties
interface ErrorWithDetails {
  code?: string;
  name?: string;
  message?: string;
}

function hasErrorDetails(error: unknown): error is ErrorWithDetails {
  return typeof error === 'object' && error !== null;
}

// Parse Amplify/Cognito errors into user-friendly messages
export const parseAuthError = (error: unknown): AppError => {
  const errorObj = hasErrorDetails(error) ? error : {};
  const errorCode = errorObj.code || errorObj.name || 'UNKNOWN_ERROR';
  const errorMessage = errorObj.message || 'An unknown error occurred';

  switch (errorCode) {
    case AUTH_ERROR_CODES.USER_NOT_FOUND:
    case AUTH_ERROR_CODES.NOT_AUTHORIZED:
      return {
        code: errorCode,
        message: errorMessage,
        userMessage: 'Invalid email or password. Please try again.',
        retryable: true
      };

    case AUTH_ERROR_CODES.USER_NOT_CONFIRMED:
      return {
        code: errorCode,
        message: errorMessage,
        userMessage: 'Please check your email and confirm your account before signing in.',
        retryable: false
      };

    case AUTH_ERROR_CODES.NEW_PASSWORD_REQUIRED:
      return {
        code: errorCode,
        message: errorMessage,
        userMessage: 'Please set a new password to continue.',
        retryable: false
      };

    case AUTH_ERROR_CODES.INVALID_PASSWORD:
      return {
        code: errorCode,
        message: errorMessage,
        userMessage: PASSWORD_POLICY_MESSAGE,
        retryable: true
      };

    case AUTH_ERROR_CODES.TOO_MANY_REQUESTS:
      return {
        code: errorCode,
        message: errorMessage,
        userMessage: 'Too many login attempts. Please wait a few minutes and try again.',
        retryable: true
      };

    case AUTH_ERROR_CODES.CODE_MISMATCH:
      return {
        code: errorCode,
        message: errorMessage,
        userMessage: 'Invalid or expired verification code. Please try again.',
        retryable: true
      };

    case AUTH_ERROR_CODES.EXPIRED_CODE:
      return {
        code: errorCode,
        message: errorMessage,
        userMessage: 'Verification code has expired. Please request a new code.',
        retryable: true
      };

    case AUTH_ERROR_CODES.LIMIT_EXCEEDED:
      return {
        code: errorCode,
        message: errorMessage,
        userMessage: 'Too many attempts. Please try again later.',
        retryable: false
      };

    default:
      return {
        code: errorCode,
        message: errorMessage,
        userMessage: 'Something went wrong. Please try again.',
        retryable: true
      };
  }
};

// Parse timeout errors
export const parseTimeoutError = (error: unknown): AppError | null => {
  if (error instanceof TimeoutError) {
    return {
      code: ERROR_CODES.TIMEOUT,
      message: error.message,
      userMessage: 'The request is taking longer than expected. Please check your connection and try again.',
      retryable: true,
    };
  }
  return null;
};

/**
 * Parse structured API errors by HTTP status code.
 *
 * Status-code dispatch is intentionally structural (not substring-based)
 * so server message wording can change without breaking the frontend's
 * understanding of what the error means.
 */
export const parseApiError = (error: unknown): AppError | null => {
  if (!(error instanceof ApiError)) {
    return null;
  }
  const { status } = error;
  if (status === 400) {
    // The files arrived but were too many or too large. Not form validation,
    // and not "didn't come through" either, so it gets its own copy.
    if (error.code === 'UPLOAD_LIMIT_EXCEEDED') {
      return {
        code: ERROR_CODES.UPLOAD_LIMIT_EXCEEDED,
        message: error.message,
        userMessage:
          `These files are over the upload limit (up to ${FILE_SIZE.MAX_FILES_PER_REPORT} PDFs, ` +
          `${FILE_SIZE.MAX_UPLOAD_MB}MB each, ${FILE_SIZE.MAX_TOTAL_UPLOAD_MB}MB total), so processing ` +
          "didn't start and no run was charged. Remove some files and submit the report again.",
        retryable: false,
      };
    }
    return {
      code: ERROR_CODES.VALIDATION_ERROR,
      message: error.message,
      userMessage: 'The information you submitted is invalid. Please review the form and try again.',
      retryable: false,
    };
  }
  if (status === 401) {
    return {
      code: ERROR_CODES.UNAUTHORIZED,
      message: error.message,
      userMessage: 'Session expired. Please log in again.',
      retryable: false,
    };
  }
  if (status === 403) {
    // The grant model emits exactly one denial code. Before it, a user out of
    // runs fell through to the generic arm below and was told their session
    // had expired, which sent them to log in again and hit the same wall.
    if (error.code === 'NO_RUNS') {
      return {
        code: ERROR_CODES.ENTITLEMENT_DENIED,
        message: error.message,
        // Deliberately does not name a place to buy them. Purchasing is not
        // available in every environment -- prod ships with an empty catalog
        // until the live move -- and the Account page says what is actually
        // on offer. Promising a purchase path that is not there would be
        // worse than the vague message this replaces.
        userMessage: "You're out of inspection runs. Check your account for options.",
        retryable: false,
      };
    }
    // A stale tab after a terms version bump. Without this arm it fell to
    // the generic one below and sent the user to log in again.
    if (error.code === 'TERMS_REQUIRED') {
      return {
        code: ERROR_CODES.TERMS_REQUIRED,
        message: error.message,
        userMessage:
          'Please review and accept the updated Terms of Service and Privacy Policy to continue.',
        retryable: false,
      };
    }
    // LIMIT_REACHED and INACTIVE were the alpha backend's codes. They are kept
    // until the alpha path retires, because EntitlementBackend still defaults
    // to alpha and a stack that has not cut over still emits them.
    if (error.code === 'LIMIT_REACHED') {
      return {
        code: ERROR_CODES.ENTITLEMENT_DENIED,
        message: error.message,
        userMessage:
          'You have reached your alpha inspection limit. Please contact PropWise to discuss expanding your access.',
        retryable: false,
      };
    }
    if (error.code === 'INACTIVE') {
      return {
        code: ERROR_CODES.ENTITLEMENT_DENIED,
        message: error.message,
        userMessage:
          "Your account isn't currently eligible for alpha access. Please contact PropWise.",
        retryable: false,
      };
    }
    return {
      code: ERROR_CODES.UNAUTHORIZED,
      message: error.message,
      userMessage: 'Session expired. Please log in again.',
      retryable: false,
    };
  }
  if (status === HTTP_STATUS.CONFLICT) {
    return {
      code: ERROR_CODES.REPORT_ID_COLLISION,
      message: error.message,
      userMessage: 'A report with this ID already exists. Please refresh and try again.',
      retryable: false,
    };
  }
  if (status === HTTP_STATUS.NOT_FOUND) {
    return {
      code: ERROR_CODES.NOT_FOUND,
      message: error.message,
      userMessage: 'Report not found.',
      retryable: false,
    };
  }
  if (status === HTTP_STATUS.TOO_MANY_REQUESTS) {
    // Not AUTH_ERROR_CODES.TOO_MANY_REQUESTS, which is Cognito's login throttle.
    // retryable keeps the manual "Try Again" button; useReports blocks the
    // automatic retry.
    return {
      code: ERROR_CODES.RATE_LIMITED,
      message: error.message,
      userMessage: 'PropWise is busy right now. Please wait a moment and try again.',
      retryable: true,
    };
  }
  if (status >= 500 && status < 600) {
    return {
      code: ERROR_CODES.SERVER_ERROR,
      message: error.message,
      userMessage: 'Something went wrong on our end. Please try again in a moment.',
      retryable: true,
    };
  }
  return null;
};

// Parse S3 upload errors
export const parseUploadError = (error: unknown): AppError | null => {
  const errorMessage = error instanceof Error ? error.message : 'Upload failed';

  // Check for cancellation first
  if (errorMessage.includes('cancelled') || errorMessage.includes('canceled')) {
    return {
      code: ERROR_CODES.UPLOAD_CANCELLED,
      message: errorMessage,
      userMessage: 'Upload cancelled.',
      retryable: false,
    };
  }

  if (errorMessage.includes('NetworkError') || errorMessage.includes('Failed to fetch')) {
    return {
      code: ERROR_CODES.NETWORK_ERROR,
      message: errorMessage,
      userMessage: 'Connection failed. Please check your internet and try again.',
      retryable: true,
    };
  }

  if (errorMessage.includes('413') || errorMessage.includes('too large')) {
    return {
      code: ERROR_CODES.FILE_TOO_LARGE,
      message: errorMessage,
      userMessage: 'File is too large. Please select a file smaller than 20MB.',
      retryable: false,
    };
  }

  if (errorMessage.includes('401') || errorMessage.includes('403')) {
    return {
      code: ERROR_CODES.UNAUTHORIZED,
      message: errorMessage,
      userMessage: 'Session expired. Please log in again.',
      retryable: false,
    };
  }

  return null;
};

// Combined error parser that tries all specific parsers
export const parseError = (error: unknown): AppError => {
  // Try specific parsers first
  const timeoutError = parseTimeoutError(error);
  if (timeoutError) return timeoutError;

  const apiError = parseApiError(error);
  if (apiError) return apiError;

  const uploadError = parseUploadError(error);
  if (uploadError) return uploadError;

  // Fallback for unknown errors
  return {
    code: ERROR_CODES.UNKNOWN,
    message: error instanceof Error ? error.message : 'Unknown error',
    userMessage: 'An unexpected error occurred. Please try again.',
    retryable: true,
  };
};

// Toast function holder for integration with ToastContext
let toastErrorFn: ((message: string) => void) | null = null;

// null on ToastProvider unmount, so showErrorToUser falls back to console.warn.
export const setToastErrorFn = (fn: ((message: string) => void) | null) => {
  toastErrorFn = fn;
};

// Display user-friendly error messages
export const showErrorToUser = (error: AppError) => {
  if (toastErrorFn) {
    toastErrorFn(error.userMessage);
  } else {
    console.warn('User Error:', error.userMessage);
  }
  return error.userMessage;
};